import React, { useMemo, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box, Paper, Typography, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Chip, Stack, Card, CardContent,
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  FormControl, InputLabel, Select, MenuItem, TextField, Alert
} from '@mui/material';
import SchoolIcon from '@mui/icons-material/School';
import PersonIcon from '@mui/icons-material/Person';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import BookIcon from '@mui/icons-material/Book';
import RoomIcon from '@mui/icons-material/Room';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import { bulkCreateSchedules, deleteSchedule, fetchClassSchedule, selectSchedules } from '../../slices/scheduleSlice';
import { fetchClassSubjects } from '../../slices/subjectsSlice';
import { fetchTeacherAbsences } from '../../slices/staffSlice';
import { useToast } from '../../context/ToastContext';
import { getPeriodLabel, PERIOD_LABELS } from '../../utils/periodLabels';

const EducationalScheduleGrid = ({
  schedules,
  periodTimings,
  selectedClass,
  selectedTeacher,
  dateFilter,
  selectedDate,
  editMode = false,
  onEditModeChange,
  classList = [],
  staffList = [],
  onScheduleUpdate
}) => {
  const dispatch = useDispatch();
  const { showToast } = useToast();
  
  // Get subjects for selected class
  const { classSubjects } = useSelector((state) => state.subjects);
  // classSubjects is an array of the last fetched class's subjects
  const subjectsList = useMemo(() => {
    if (selectedClass === 'all' || !Array.isArray(classSubjects)) return [];
    return classSubjects.filter(s => s.school_class === selectedClass && !s.is_archived);
  }, [classSubjects, selectedClass]);
  
  // Get teacher absences for the selected date
  const { teacherAbsences } = useSelector((state) => state.staff);
  
  // Fetch absences when date changes (for daily view)
  useEffect(() => {
    if (selectedDate && (dateFilter === 'today' || dateFilter === 'daily' || dateFilter === 'yesterday' || dateFilter === 'tomorrow')) {
      const dateStr = selectedDate.toISOString().split('T')[0];
      dispatch(fetchTeacherAbsences({ absence_date: dateStr }));
    }
  }, [selectedDate, dateFilter, dispatch]);
  
  // Get absent teacher IDs for the selected date
  const absentTeacherIds = useMemo(() => {
    if (!selectedDate) return [];
    const dateStr = selectedDate.toISOString().split('T')[0];
    return teacherAbsences
      .filter(a => a.absence_date === dateStr)
      .map(a => a.teacher);
  }, [teacherAbsences, selectedDate]);
  
  // Edit dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingCell, setEditingCell] = useState(null);
  const [editFormData, setEditFormData] = useState({
    subject: '',
    teacher: '',
    classroom: '',
    notes: ''
  });
  
  // Loading state
  const { loading } = useSelector((state) => state.schedule);
  const allSchedules = useSelector(selectSchedules);
  
  // Load subjects when class is selected and dialog opens
  useEffect(() => {
    if (editDialogOpen && selectedClass !== 'all') {
      dispatch(fetchClassSubjects(selectedClass));
    }
  }, [editDialogOpen, selectedClass, dispatch]);
  // Get day name in Arabic
  const getDayName = (dayValue) => {
    const days = {
      'sunday': 'الأحد',
      'monday': 'الاثنين',
      'tuesday': 'الثلاثاء',
      'wednesday': 'الأربعاء',
      'thursday': 'الخميس',
      'friday': 'الجمعة',
      'saturday': 'السبت'
    };
    return days[dayValue] || dayValue;
  };

  // Group schedules by day and time slot
  const scheduleGrid = useMemo(() => {
    const grid = {};
    const dayOrder = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

    // Initialize grid
    dayOrder.forEach(day => {
      grid[day] = {};
    });

    // Populate grid
    schedules.forEach(schedule => {
      const day = schedule.day_of_week;
      const slotId = schedule.slot_id || schedule.start_time;
      if (grid[day] && slotId) {
        if (!grid[day][slotId]) {
          grid[day][slotId] = [];
        }
        grid[day][slotId].push(schedule);
      }
    });

    return grid;
  }, [schedules]);

  // Get time slots
  const timeSlots = useMemo(() => {
    if (periodTimings && periodTimings.length > 0) {
      return periodTimings
        .filter(t => t.is_active)
        .sort((a, b) => (a.start_time || '').localeCompare(b.start_time || ''));
    }
    // Default time slots
    return [
      { id: 'period_1', start_time: '08:00', end_time: '08:45' },
      { id: 'period_2', start_time: '08:45', end_time: '09:30' },
      { id: 'period_3', start_time: '09:30', end_time: '10:15' },
      { id: 'period_4', start_time: '10:30', end_time: '11:15' },
      { id: 'period_5', start_time: '11:15', end_time: '12:00' }
    ];
  }, [periodTimings]);

  // Handle cell click for editing
  const handleCellClick = (day, slotId) => {
    if (!editMode || selectedClass === 'all') return;
    
    const slotSchedules = scheduleGrid[day]?.[slotId] || [];
    const slot = timeSlots.find(s => (s.id || s.period) === slotId || s.start_time === slotId);
    
    setEditingCell({
      day,
      slotId,
      slot,
      schedules: slotSchedules
    });
    
    // Pre-fill form if there's an existing schedule
    if (slotSchedules.length > 0) {
      const existingSchedule = slotSchedules[0];
      setEditFormData({
        subject: existingSchedule.subject || existingSchedule.subject_id || '',
        teacher: existingSchedule.teacher || existingSchedule.teacher_id || '',
        classroom: existingSchedule.classroom || '',
        notes: existingSchedule.notes || ''
      });
    } else {
      setEditFormData({
        subject: '',
        teacher: '',
        classroom: '',
        notes: ''
      });
    }
    
    setEditDialogOpen(true);
  };

  // Auto-fill teacher, classroom and notes when a subject is selected
  const handleSubjectChange = (subjectId) => {
    const subject = subjectsList.find(s => s.id === subjectId);
    const getId = (v) => (v && typeof v === 'object' ? v.id : v);
    // Reuse details from another period of the same subject in this class
    // (use the unfiltered store list, the `schedules` prop is narrowed by day/teacher filters)
    const sameSubjectSchedule = allSchedules.find(s =>
      String(getId(s.subject) || s.subject_id) === String(subjectId) &&
      (!s.school_class || String(getId(s.school_class)) === String(selectedClass))
    );

    const teacherId = getId(subject?.teacher) || getId(sameSubjectSchedule?.teacher) || sameSubjectSchedule?.teacher_id;
    const isValidTeacher = staffList.some(t => t.is_teacher && !t.is_archived && t.id === teacherId);

    setEditFormData(prev => ({
      ...prev,
      subject: subjectId,
      teacher: isValidTeacher ? teacherId : prev.teacher,
      classroom: sameSubjectSchedule?.classroom || prev.classroom,
      notes: sameSubjectSchedule?.notes || prev.notes
    }));
  };

  // Handle save schedule
  const handleSaveSchedule = async () => {
    if (!editingCell || selectedClass === 'all') return;
    
    if (!editFormData.subject) {
      showToast('يرجى اختيار مادة', 'warning');
      return;
    }
    
    const subject = subjectsList.find(s => s.id === editFormData.subject);
    if (!subject) {
      showToast('المادة المختارة غير موجودة', 'error');
      return;
    }
    
    const slot = editingCell.slot;
    const scheduleData = {
      day_of_week: editingCell.day,
      slot_id: editingCell.slotId,
      slot_name: getPeriodLabel(slot) || editingCell.slotId,
      slot_type: 'class',
      start_time: slot?.start_time || '08:00',
      end_time: slot?.end_time || '08:45',
      school_class_id: selectedClass,
      subject_id: editFormData.subject,
      teacher_id: editFormData.teacher || null,
      classroom: editFormData.classroom || '',
      notes: editFormData.notes || ''
    };
    
    try {
      // The bulk endpoint replaces the class's whole timetable (periods missing from the
      // payload are deleted), so send every existing period with only this cell changed
      const fetched = await dispatch(fetchClassSchedule(selectedClass));
      if (!fetched.type.endsWith('/fulfilled')) {
        showToast('تعذر تحميل جدول الصف، لم يتم حفظ التغيير', 'error');
        return;
      }
      const getId = (v) => (v && typeof v === 'object' ? v.id : v);
      const otherSchedules = (fetched.payload.schedules || [])
        .filter(s => !(s.day_of_week === editingCell.day && String(s.slot_id) === String(editingCell.slotId)))
        .map(s => ({
          day_of_week: s.day_of_week,
          slot_id: s.slot_id,
          slot_name: PERIOD_LABELS[s.slot_name] || s.slot_name,
          slot_type: s.slot_type || 'class',
          start_time: s.start_time,
          end_time: s.end_time,
          school_class_id: selectedClass,
          subject_id: getId(s.subject) || null,
          teacher_id: getId(s.teacher) || null,
          classroom: s.classroom || '',
          notes: s.notes || '',
          ...(s.color ? { color: s.color } : {})
        }));

      const result = await dispatch(bulkCreateSchedules({
        school_class_id: selectedClass,
        schedules: [...otherSchedules, scheduleData],
        ignore_conflicts: true
      }));
      
      // The endpoint skips rows it fails to save and still returns 2xx with an errors list
      const saveErrors = result.payload?.errors || [];
      if (result.type.endsWith('/fulfilled') && saveErrors.length > 0) {
        console.error('Schedule save errors:', saveErrors);
        showToast('تم الحفظ مع وجود أخطاء في بعض الحصص', 'warning');
        if (onScheduleUpdate) {
          onScheduleUpdate();
        }
      } else if (result.type.endsWith('/fulfilled')) {
        showToast('تم تحديث الجدول بنجاح', 'success');
        setEditDialogOpen(false);
        setEditingCell(null);
        if (onScheduleUpdate) {
          onScheduleUpdate();
        }
      } else {
        showToast('حدث خطأ أثناء تحديث الجدول', 'error');
      }
    } catch (error) {
      console.error('Error saving schedule:', error);
      showToast('حدث خطأ أثناء تحديث الجدول', 'error');
    }
  };

  // Handle delete schedule
  const handleDeleteSchedule = async () => {
    if (!editingCell || editingCell.schedules.length === 0) return;
    if (!window.confirm('هل أنت متأكد من حذف هذه الحصة من الجدول الأصلي؟')) return;
    
    try {
      const results = await Promise.all(
        editingCell.schedules
          .filter(s => s.id)
          .map(s => dispatch(deleteSchedule(s.id)))
      );
      
      if (results.every(r => r.type.endsWith('/fulfilled'))) {
        showToast('تم حذف الحصة بنجاح', 'success');
        setEditDialogOpen(false);
        setEditingCell(null);
      } else {
        showToast('حدث خطأ أثناء حذف الحصة', 'error');
      }
      if (onScheduleUpdate) {
        onScheduleUpdate();
      }
    } catch (error) {
      console.error('Error deleting schedule:', error);
      showToast('حدث خطأ أثناء حذف الحصة', 'error');
    }
  };

  // Render schedule cell
  const renderScheduleCell = (day, slotId) => {
    const slotSchedules = scheduleGrid[day]?.[slotId] || [];
    const isClickable = editMode && selectedClass !== 'all';
    
    if (slotSchedules.length === 0) {
      return (
        <TableCell 
          sx={{ 
            minHeight: 80, 
            textAlign: 'center', 
            color: 'text.secondary',
            cursor: isClickable ? 'pointer' : 'default',
            '&:hover': isClickable ? { bgcolor: 'action.hover' } : {}
          }}
          onClick={() => isClickable && handleCellClick(day, slotId)}
        >
          {isClickable ? <AddIcon sx={{ opacity: 0.5 }} /> : '-'}
        </TableCell>
      );
    }

    return (
      <TableCell 
        sx={{ 
          minHeight: 80, 
          p: 1,
          cursor: isClickable ? 'pointer' : 'default',
          '&:hover': isClickable ? { bgcolor: 'action.hover' } : {}
        }}
        onClick={() => isClickable && handleCellClick(day, slotId)}
      >
        <Stack spacing={0.5}>
          {slotSchedules.map((schedule, idx) => (
            <Card
              key={idx}
              elevation={1}
              sx={{
                p: 1,
                bgcolor: schedule.color ? `${schedule.color}15` : 'primary.light',
                border: `1px solid ${schedule.color || 'primary.main'}30`,
                borderRadius: 1,
                position: 'relative'
              }}
            >
              {isClickable && (
                <EditIcon 
                  sx={{ 
                    position: 'absolute', 
                    top: 4, 
                    left: 4, 
                    fontSize: 16, 
                    opacity: 0.7 
                  }} 
                />
              )}
              <Stack spacing={0.5}>
                <Typography variant="caption" fontWeight="bold">
                  {schedule.subject_name || 'غير محدد'}
                </Typography>
                {schedule.school_class_name && (
                  <Stack useFlexGap direction="row" spacing={0.5} alignItems="center">
                    <SchoolIcon sx={{ fontSize: 12 }} />
                    <Typography variant="caption" fontSize="0.7rem">
                      {schedule.school_class_name}
                    </Typography>
                  </Stack>
                )}
                {schedule.teacher_name && (
                  <Stack useFlexGap direction="row" spacing={0.5} alignItems="center">
                    <PersonIcon sx={{ fontSize: 12 }} />
                    <Typography variant="caption" fontSize="0.7rem">
                      {schedule.teacher_name}
                    </Typography>
                  </Stack>
                )}
                {schedule.classroom && (
                  <Stack useFlexGap direction="row" spacing={0.5} alignItems="center">
                    <RoomIcon sx={{ fontSize: 12 }} />
                    <Typography variant="caption" fontSize="0.7rem">
                      {schedule.classroom}
                    </Typography>
                  </Stack>
                )}
              </Stack>
            </Card>
          ))}
        </Stack>
      </TableCell>
    );
  };

  if (schedules.length === 0) {
    return (
      <Paper sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h6" color="text.secondary">
          لا توجد جداول متاحة
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          يرجى اختيار فلاتر مختلفة أو إضافة جداول جديدة
        </Typography>
      </Paper>
    );
  }

  return (
    <>
      {editMode && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          وضع التعديل مفعّل - انقر على أي خلية لتعديلها. سيتم تطبيق التغييرات على الجدول الأصلي.
        </Alert>
      )}
      
      <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 2 }}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: 'primary.main' }}>
              <TableCell sx={{ color: 'white', fontWeight: 'bold', minWidth: 100 }}>
                الوقت
              </TableCell>
              {Object.keys(scheduleGrid).map(day => (
                <TableCell
                  key={day}
                  sx={{ color: 'white', fontWeight: 'bold', textAlign: 'center', minWidth: 150 }}
                >
                  {getDayName(day)}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {timeSlots.map((slot) => (
              <TableRow key={slot.id || slot.start_time} hover>
                <TableCell sx={{ fontWeight: 'bold', bgcolor: 'grey.50' }}>
                  <Stack spacing={0.5}>
                    <Typography variant="body2" fontWeight="bold">
                      {slot.start_time} - {slot.end_time}
                    </Typography>
                    {getPeriodLabel(slot) && (
                      <Typography variant="caption" color="text.secondary">
                        {getPeriodLabel(slot)}
                      </Typography>
                    )}
                  </Stack>
                </TableCell>
                {Object.keys(scheduleGrid).map(day => (
                  <React.Fragment key={day}>
                    {renderScheduleCell(day, slot.id || slot.start_time)}
                  </React.Fragment>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Edit Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, direction: 'rtl' } }}
      >
        <DialogTitle>
          {editingCell?.schedules.length > 0 ? 'تعديل الجدول' : 'إضافة جدول'}
          {editingCell && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {getDayName(editingCell.day)} - {editingCell.slot?.start_time} - {editingCell.slot?.end_time}
            </Typography>
          )}
          <Alert severity="warning" sx={{ mt: 1, py: 0.5 }}>
            ⚠️ سيتم تطبيق التغييرات على الجدول الأصلي مباشرة
          </Alert>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>المادة *</InputLabel>
              <Select
                value={editFormData.subject && subjectsList.some(s => s.id === editFormData.subject) ? editFormData.subject : ''}
                onChange={(e) => handleSubjectChange(e.target.value)}
                label="المادة *"
                disabled={loading.bulkCreating}
              >
                {subjectsList.length === 0 ? (
                  <MenuItem disabled>جاري تحميل المواد...</MenuItem>
                ) : (
                  subjectsList.map((subject) => (
                    <MenuItem key={subject.id} value={subject.id}>
                      {subject.name}
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>المعلم</InputLabel>
              <Select
                value={editFormData.teacher && staffList.some(t => t.id === editFormData.teacher) ? editFormData.teacher : ''}
                onChange={(e) => setEditFormData({ ...editFormData, teacher: e.target.value })}
                label="المعلم"
              >
                <MenuItem value="">لا يوجد</MenuItem>
                {staffList
                  .filter(s => s.is_teacher && !s.is_archived)
                  .map((teacher) => (
                    <MenuItem key={teacher.id} value={teacher.id}>
                      {teacher.first_name} {teacher.last_name}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>

            <TextField
              label="الفصل/القاعة"
              value={editFormData.classroom}
              onChange={(e) => setEditFormData({ ...editFormData, classroom: e.target.value })}
              fullWidth
            />

            <TextField
              label="ملاحظات"
              value={editFormData.notes}
              onChange={(e) => setEditFormData({ ...editFormData, notes: e.target.value })}
              fullWidth
              multiline
              rows={3}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          {editingCell?.schedules.length > 0 && (
            <Button
              onClick={handleDeleteSchedule}
              color="error"
              startIcon={<DeleteIcon />}
              disabled={loading.bulkCreating}
            >
              حذف
            </Button>
          )}
          <Button
            onClick={() => setEditDialogOpen(false)}
            variant="outlined"
            startIcon={<CancelIcon />}
          >
            إلغاء
          </Button>
          <Button
            onClick={handleSaveSchedule}
            variant="contained"
            startIcon={<SaveIcon />}
            disabled={loading.bulkCreating || !editFormData.subject}
          >
            حفظ
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default EducationalScheduleGrid;
